/**
 * History Rail — the timeline (HR-1).
 *
 * DOM-first throughout: every server string lands via `textContent`, so there
 * is no `escapeHTML` call to forget. The modal built rows from an HTML template
 * literal and had to escape seven values by hand.
 *
 * Row order is contract §5 — time, author, summary, then the version number as
 * a dimmed trailing chip. Version numbers do not lead: nobody remembers "it was
 * good at v14", they remember "it was good before lunch" and "it was good
 * before Maya touched the hero."
 *
 * NOT an `openCollectionBrowser` consumer, and this is a deliberate divergence
 * from the picker-patterns rule rather than an oversight: that primitive owns a
 * modal frame — overlay, focus trap, close affordance — and the rail is not a
 * modal. What the rule is really protecting (kit primitives, one disclosure
 * idiom, no hand-rolled chrome) is honoured directly: buttons come from
 * `createButton`, glyphs from `icon()`, and the end-of-list disclosure is the
 * same "Load older versions" affordance with the same busy semantics.
 */

import { createButton } from '../components.js';
import { clockTime, dayKeyOf, dayLabelOf } from './version-model.js';

/**
 * @param {object} opts
 * @param {HTMLElement} opts.host — the rail body
 * @param {ReturnType<import('./version-model.js').createVersionModel>} opts.model
 * @param {(version: number|null) => void} opts.onSelect — null clears to live state
 * @param {() => {dirty: boolean, projectName: string}} opts.liveState
 * @param {() => void} opts.onSave
 */
export function mountTimeline({
  host,
  model,
  onSelect,
  liveState,
  onSave,
  onHover,
  onRename,
  onCommit,
  canCommit
}) {
  let selected = null;
  let milestonesOnly = false;
  let destroyed = false;
  /** Held-key scrub coalescing — see the keydown handler. */
  let scrubTimer = null;
  /**
   * version → the filled change caption.
   *
   * Kept OUTSIDE the DOM because `render()` rebuilds every row from scratch —
   * unfolding a run, switching filter or loading older versions would otherwise
   * silently reset every caption to the size placeholder. That is exactly what
   * HR-5's browser pass caught: after two fold clicks every row read '402.3 KB'
   * again. The modal solved the same problem by holding its change panels in a
   * Map for the surface's lifetime.
   */
  const summaries = new Map();

  const root = document.createElement('div');
  root.className = 'pb-rail-timeline-root';

  // `variant: 'bare'` throughout this file: the kit's bare variant adds no base
  // class, which is exactly right for controls that carry a fully-custom surface
  // (the Now card, a timeline row, a fold divider) while still getting the kit's
  // aria wiring, busy state and single-flight guard. Hand-rolling
  // `createElement('button')` skips all of that, and the UI consistency sweep
  // says so by name.
  const nowCard = createButton({
    variant: 'bare',
    className: 'pb-rail-now',
    onClick: (event) => {
      if (event?.target?.closest?.('[data-action="save-now"]')) return;
      select(null);
    }
  });

  const filterRow = document.createElement('div');
  filterRow.className = 'pb-rail-filter';
  const seg = document.createElement('div');
  seg.className = 'pb-rail-seg';
  seg.setAttribute('role', 'group');
  seg.setAttribute('aria-label', 'Filter versions');
  for (const [key, label] of [
    ['all', 'All'],
    ['mile', 'Milestones']
  ]) {
    const b = createButton({
      variant: 'bare',
      label,
      dataset: { filter: key },
      onClick: () => {
        milestonesOnly = key === 'mile';
        for (const other of seg.querySelectorAll('button')) {
          other.setAttribute('aria-pressed', String(other === b));
        }
        render();
      }
    });
    b.setAttribute('aria-pressed', String(key === 'all'));
    seg.appendChild(b);
  }
  filterRow.appendChild(seg);

  const list = document.createElement('div');
  list.className = 'pb-rail-timeline';
  list.setAttribute('role', 'listbox');
  list.setAttribute('aria-label', 'Versions');
  list.tabIndex = 0;

  const foot = document.createElement('div');
  foot.className = 'pb-rail-foot';

  /**
   * Detents (HR-6). Three heights, cycled by the handle. Below 900px the rail
   * is a bottom dock and this is how tall it is; on desktop the handle is
   * `display: none` because a fixed-width rail has nothing to resize.
   */
  const DETENTS = ['peek', 'half', 'full'];
  const grab = createButton({
    variant: 'bare',
    className: 'pb-rail-grab',
    ariaLabel: 'Resize history',
    onClick: () => setDetent(DETENTS[(DETENTS.indexOf(detent()) + 1) % DETENTS.length])
  });
  grab.append(document.createElement('i'));

  /**
   * The commit footer. Restore lives here on touch rather than in the review
   * bar: bottom-of-screen is the cheapest target on a phone and a per-row button
   * in a scrolling list is the most expensive. It is `is-armed` only once a
   * version is selected, so the surface never offers a destructive verb with no
   * object.
   */
  const commit = document.createElement('div');
  commit.className = 'pb-rail-foot-commit';
  const commitBtn = createButton({
    variant: 'primary',
    label: 'Restore this version',
    className: 'pb-rail-commit',
    dataset: { action: 'restore' },
    onClick: () => onCommit?.(selected)
  });
  const moreBtn = createButton({
    variant: 'outline',
    label: '⋯',
    className: 'pb-rail-more',
    ariaLabel: 'Name this version',
    tooltip: 'Name this version',
    // The touch answer to double-click. `dblclick` has no touch analogue at
    // all, and long-press fights the scroll gesture in a list you scroll with
    // the same finger — so naming gets a real, visible control instead.
    onClick: () => onRename?.(selected)
  });
  commit.append(commitBtn, moreBtn);

  // A SIBLING of `foot`, not a child of it. `renderFoot()` calls
  // `replaceChildren()` to swap the load-older disclosure, which destroys any
  // persistent chrome sharing that container — the commit footer vanished
  // entirely until the mobile browser pass looked for it. This is the third
  // time in this initiative that a render has wiped state it did not own
  // (summaries, the fallback notice, this); the fix is always the same, which
  // is to stop putting durable things inside a container that gets rebuilt.
  root.append(grab, nowCard, filterRow, list, foot, commit);
  host.replaceChildren(root);

  // ── selection ──────────────────────────────────────────────

  /**
   * @param {number|null} version
   * @param {{settle?: number}} [opts] — ms to wait before telling anyone
   */
  function select(version, { settle = 0 } = {}) {
    selected = version === null ? null : Number(version);
    // The SELECTION paints immediately, always. Only the expensive consequence
    // is deferred: feedback must never wait on a fetch.
    paintSelection();
    clearTimeout(scrubTimer);
    if (!settle) {
      onSelect?.(selected);
      return;
    }
    scrubTimer = setTimeout(() => {
      scrubTimer = null;
      onSelect?.(selected);
    }, settle);
  }

  function detent() {
    return host.closest('.pb-panel-right')?.dataset.detent || 'half';
  }

  function setDetent(next) {
    const slot = host.closest('.pb-panel-right');
    if (!slot) return;
    slot.dataset.detent = next;
    grab.setAttribute('aria-label', `Resize history (${next})`);
  }

  function paintSelection() {
    nowCard.classList.toggle('is-selected', selected === null);
    // The commit control arms only with a version selected, and carries its own
    // reason when it cannot act — never live-and-inert.
    const blocked = selected === null ? 'no version selected' : canCommit?.() || null;
    commit.classList.toggle('is-armed', selected !== null);
    commitBtn.disabled = Boolean(blocked);
    if (blocked && selected !== null) commitBtn.title = blocked;
    else commitBtn.removeAttribute('title');
    for (const row of list.querySelectorAll('.pb-rail-row')) {
      const on = Number(row.dataset.version) === selected;
      row.classList.toggle('is-selected', on);
      row.setAttribute('aria-selected', String(on));
    }
  }

  list.addEventListener('click', (event) => {
    const fold = event.target.closest('[data-action="unfold"]');
    if (fold) {
      // Unfold reveals in place: the run's rows stop being folded tails and the
      // note retires. Selection is untouched.
      model.toggleFold(fold.dataset.run);
      render();
      return;
    }
    const row = event.target.closest('.pb-rail-row');
    if (row) select(row.dataset.version);
  });

  list.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const rows = [...list.querySelectorAll('.pb-rail-row')];
    if (!rows.length) return;
    event.preventDefault();
    const at = rows.findIndex((r) => Number(r.dataset.version) === selected);
    const next =
      event.key === 'ArrowDown'
        ? rows[Math.min(rows.length - 1, at + 1)]
        : at <= 0
          ? null
          : rows[at - 1];
    // Stepping up past the first row lands on Now — the timeline's zero point
    // is the present, so "up" from the newest version is where it must go.
    if (!next) {
      select(null);
      return;
    }
    // COALESCED. Holding ↓ through ten versions must cost ONE bundle fetch, at
    // the version you stop on — not ten, nine of which are already stale before
    // they land. The row highlight still moves on every keypress; it is only the
    // download that waits for you to settle.
    select(next.dataset.version, { settle: 220 });
    next.scrollIntoView({ block: 'nearest' });
  });

  /**
   * Warm the version-state cache on hover.
   *
   * This is what lets the modal's "Comparing… (downloads ~340 KB)" copy be
   * DELETED rather than hidden: by the time the click lands the data is usually
   * already there, so there is no cost to disclose. Quoting a user the byte
   * price of reading their own history teaches them to stop being curious.
   */
  list.addEventListener('pointerover', (event) => {
    const row = event.target.closest?.('.pb-rail-row');
    if (row?.dataset.version) onHover?.(Number(row.dataset.version));
  });

  // Naming a version is how a save becomes a Milestone. `labelProjectVersion()`
  // has shipped since UX Pack F1b and the modal never offered it anywhere.
  list.addEventListener('dblclick', (event) => {
    const row = event.target.closest?.('.pb-rail-row');
    if (row?.dataset.version) onRename?.(Number(row.dataset.version));
  });

  // ── render ─────────────────────────────────────────────────

  function renderNowCard() {
    const { dirty } = liveState();
    nowCard.replaceChildren();

    const text = document.createElement('span');
    const label = document.createElement('span');
    label.className = 'pb-rail-now-label';
    label.textContent = 'Now';
    const state = document.createElement('span');
    state.className = 'pb-rail-now-state';
    // Stated at the top of the rail, on open. In the modal this fact only
    // appeared inside the restore confirm — at the moment it is most alarming
    // and least actionable.
    state.textContent = dirty ? 'Unsaved changes' : 'All changes saved';
    state.classList.toggle('is-dirty', dirty);
    text.append(label, state);
    nowCard.appendChild(text);

    if (dirty) {
      nowCard.appendChild(
        createButton({
          variant: 'ghost',
          size: 'sm',
          label: 'Save',
          className: 'pb-rail-now-save',
          onClick: () => onSave?.()
        })
      );
    }

    const publish = model.publishState();
    if (publish) {
      const bar = document.createElement('span');
      bar.className = `pb-rail-pubstate${publish.upToDate ? ' is-current' : ''}`;
      bar.textContent = publish.upToDate
        ? `Up to date with the last publish (v${publish.version})`
        : `Unpublished changes since v${publish.version} · ${publish.kind}`;
      nowCard.appendChild(bar);
    }
  }

  function buildRow(v) {
    const fold = model.foldFor(v.version);
    const row = createButton({
      variant: 'bare',
      className: 'pb-rail-row',
      role: 'option',
      dataset: { version: String(v.version) }
    });
    row.setAttribute('aria-selected', 'false');

    const spine = document.createElement('span');
    spine.className = 'pb-rail-spine';
    const node = document.createElement('span');
    node.className = 'pb-rail-node';
    if (model.isMilestone(v)) node.classList.add('is-milestone');
    spine.appendChild(node);

    const body = document.createElement('span');
    body.className = 'pb-rail-row-body';

    const top = document.createElement('span');
    top.className = 'pb-rail-row-top';
    const time = document.createElement('span');
    time.className = 'pb-rail-time';
    time.textContent = clockTime(v.createdAt);
    const who = document.createElement('span');
    who.className = 'pb-rail-who';
    // HR-1 ships without an author when the row has none. `project_versions`
    // carries version/r2_key/size_bytes/hash/label/created_at/content_hash
    // (ai/project-handlers.js) and a creator column was NOT confirmed — the
    // plan's §4 risk table says ship the row without it rather than block.
    who.textContent = v.createdByName || v.author || '';
    const num = document.createElement('span');
    num.className = 'pb-rail-num';
    num.textContent = `v${v.version}`;
    top.append(time, who, num);

    if (v.label) {
      const name = document.createElement('span');
      name.className = 'pb-rail-name';
      name.textContent = v.label;
      body.append(top, name);
    } else {
      body.appendChild(top);
    }

    const summary = document.createElement('span');
    summary.className = 'pb-rail-summary';
    // Always visible, never behind a caret (contract §5). The size is only a
    // placeholder until the diff lands; a filled caption survives re-renders.
    summary.textContent =
      summaries.get(Number(v.version)) || `${((v.sizeBytes || 0) / 1024).toFixed(1)} KB`;
    summary.dataset.role = 'summary';
    body.appendChild(summary);

    const chips = document.createElement('span');
    chips.className = 'pb-rail-chips';
    if (fold.latest) chips.appendChild(chip('Latest', 'is-latest'));
    if (model.publishesFor(v.version).length) {
      const c = chip('Published', 'is-published');
      c.dataset.tooltip = model.publishDetail(v.version);
      chips.appendChild(c);
    }
    if (v.label) chips.appendChild(chip('Milestone', 'is-milestone'));
    if (chips.childElementCount) body.appendChild(chips);

    row.append(spine, body);
    return { row, fold };
  }

  function chip(text, className) {
    const el = document.createElement('span');
    el.className = `pb-rail-chip ${className}`;
    el.textContent = text;
    return el;
  }

  /**
   * A hairline divider, not a control in the row's action slot: it reads as
   * compressed time, which is what it is.
   *
   * It TOGGLES. The modal's version hardcoded `aria-expanded="false"` and then
   * removed itself on click, so it was never a disclosure at all — no way back,
   * and nothing announced.
   *
   * @param {string} runKey
   * @param {number} hidden — rows currently folded away (0 when expanded)
   * @param {boolean} expanded
   */
  function foldNote(runKey, hidden, expanded) {
    const btn = createButton({
      variant: 'bare',
      className: 'pb-rail-fold',
      ariaExpanded: expanded,
      dataset: { action: 'unfold', run: runKey }
    });
    const line = document.createElement('span');
    line.className = 'pb-rail-fold-line';
    const count = document.createElement('span');
    count.className = 'pb-rail-fold-count';
    count.textContent = expanded
      ? 'Fold identical saves'
      : `${hidden} identical save${hidden === 1 ? '' : 's'}`;
    const line2 = document.createElement('span');
    line2.className = 'pb-rail-fold-line';
    btn.append(line, count, line2);
    return btn;
  }

  function render() {
    if (destroyed) return;
    renderNowCard();

    const rows = model.visibleVersions({ milestonesOnly });
    list.replaceChildren();

    if (!rows.length) {
      const empty = document.createElement('p');
      empty.className = 'pb-rail-placeholder';
      empty.textContent = milestonesOnly
        ? 'No milestones yet. Name a version to mark one.'
        : 'No saved versions yet. Every cloud save adds one here.';
      list.appendChild(empty);
      renderFoot();
      return;
    }

    let day = null;
    let group = null;
    for (const v of rows) {
      const key = dayKeyOf(v.createdAt);
      if (key !== day) {
        day = key;
        const head = document.createElement('div');
        head.className = 'pb-rail-day';
        head.textContent = dayLabelOf(v.createdAt);
        list.appendChild(head);
        group = document.createElement('div');
        group.className = 'pb-rail-day-rows';
        list.appendChild(group);
      }
      const { row, fold } = buildRow(v);
      group.appendChild(row);
      if (milestonesOnly || !fold.runKey) continue;
      // Folded: the note sits under the run head and says what is hidden.
      if (fold.foldCount > 0) {
        group.appendChild(foldNote(fold.runKey, fold.foldCount, false));
        continue;
      }
      // Expanded: the note sits under the LAST row of the run and offers the
      // way back. `runSize > 1` keeps it off runs that were never folded.
      if (model.isUnfolded(fold.runKey) && model.runSize(fold.runKey) > 1) {
        const isLastOfRun = !rows[rows.indexOf(v) + 1]
          ? true
          : model.foldFor(rows[rows.indexOf(v) + 1].version).runKey !== fold.runKey;
        if (isLastOfRun) group.appendChild(foldNote(fold.runKey, 0, true));
      }
    }

    paintSelection();
    renderFoot();
  }

  function renderFoot() {
    foot.replaceChildren();
    if (!model.hasMore || milestonesOnly) return;
    const btn = createButton({
      variant: 'ghost',
      label: 'Load older versions',
      className: 'pb-rail-load-older',
      onAsyncClick: async () => {
        await model.loadOlder();
        render();
      },
      busyLabel: 'Loading…',
      errorScope: 'History rail load older'
    });
    foot.appendChild(btn);
  }

  function renderSkeleton(count = 5) {
    list.replaceChildren();
    const wrap = document.createElement('div');
    wrap.className = 'pb-rail-day-rows';
    for (let i = 0; i < count; i += 1) {
      const sk = document.createElement('div');
      sk.className = 'pb-rail-row is-skeleton';
      sk.setAttribute('aria-hidden', 'true');
      const spine = document.createElement('span');
      spine.className = 'pb-rail-spine';
      spine.appendChild(
        Object.assign(document.createElement('span'), { className: 'pb-rail-node' })
      );
      const body = document.createElement('span');
      body.className = 'pb-rail-row-body';
      body.append(
        Object.assign(document.createElement('span'), { className: 'pb-rail-sk pb-rail-sk-top' }),
        Object.assign(document.createElement('span'), { className: 'pb-rail-sk pb-rail-sk-sub' })
      );
      sk.append(spine, body);
      wrap.appendChild(sk);
    }
    list.appendChild(wrap);
  }

  function renderError(message) {
    list.replaceChildren();
    const p = document.createElement('p');
    p.className = 'pb-rail-placeholder is-error';
    p.textContent = message || 'Could not load versions.';
    list.appendChild(p);
  }

  /** Focus the newest row — the answer most of the time, and it makes ↓ mean something. */
  function focusNewest() {
    const first = list.querySelector('.pb-rail-row');
    (first || list).focus();
  }

  return {
    render,
    renderSkeleton,
    renderError,
    focusNewest,
    select,
    get selected() {
      return selected;
    },
    /**
     * The element an inline rename replaces. A row with no label yet has no
     * `.pb-rail-name`, so one is created on demand — otherwise naming would
     * only work on versions that already had a name.
     */
    labelHostFor(version) {
      const row = list.querySelector(`.pb-rail-row[data-version="${version}"]`);
      if (!row) return null;
      const existing = row.querySelector('.pb-rail-name');
      if (existing) return existing;
      const host = document.createElement('span');
      host.className = 'pb-rail-name';
      row.querySelector('.pb-rail-row-top')?.after(host);
      return host;
    },

    /** Fill a row's summary with what that version changed (HR-5). */
    setSummary(version, text) {
      if (!text) return;
      summaries.set(Number(version), text);
      const el = list.querySelector(
        `.pb-rail-row[data-version="${version}"] [data-role="summary"]`
      );
      if (el) el.textContent = text;
    },
    setDetent,
    get detent() {
      return detent();
    },

    destroy() {
      destroyed = true;
      clearTimeout(scrubTimer);
      host.replaceChildren();
    }
  };
}
